'use client';

import type { ReactNode, RefObject } from 'react';
import { useIsDesktop } from '../lib/hooks/use-is-desktop';
import { cn } from '../lib/utils';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from './ui/drawer';

export function ResponsiveDialog({
  isOpen,
  onOpenChange,
  children,
}: {
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  children: ReactNode;
}) {
  const isDesktop = useIsDesktop();
  const Component = isDesktop ? Dialog : Drawer;

  return (
    <Component
      open={isOpen ?? undefined}
      onOpenChange={onOpenChange ?? undefined}
    >
      {children}
    </Component>
  );
}

export function ResponsiveDialogTrigger({
  children,
  asChild = false,
  className,
}: {
  children: ReactNode;
  asChild?: boolean;
  className?: string;
}) {
  const isDesktop = useIsDesktop();
  const Component = isDesktop ? DialogTrigger : DrawerTrigger;

  return (
    <Component asChild={asChild} className={className}>
      {children}
    </Component>
  );
}

export function ResponsiveDialogContent({
  children,
  title,
  titleClassName,
  description,
  descriptionClassName,
  showDescription = true,
  className,
  onInteractOutside,
  ref,
}: {
  children: ReactNode;
  title: string;
  titleClassName?: string;
  description: string;
  descriptionClassName?: string;
  showDescription?: boolean;
  className?: string;
  onInteractOutside?: (e: Event) => void;
  ref?: RefObject<HTMLDivElement | null>;
}) {
  const isDesktop = useIsDesktop();
  const Component = isDesktop ? DialogContent : DrawerContent;
  const Header = isDesktop ? DialogHeader : DrawerHeader;
  const Title = isDesktop ? DialogTitle : DrawerTitle;
  const Description = isDesktop ? DialogDescription : DrawerDescription;

  return (
    <Component
      onInteractOutside={onInteractOutside}
      className={className}
      ref={ref}
    >
      <Header>
        <Title className={titleClassName}>{title}</Title>
        <Description
          className={cn(!showDescription && 'sr-only', descriptionClassName)}
        >
          {description}
        </Description>
      </Header>
      {children}
    </Component>
  );
}

export function ResponsiveDialogClose({
  children,
  asChild = false,
}: {
  children: ReactNode;
  asChild?: boolean;
}) {
  const isDesktop = useIsDesktop();
  const Component = isDesktop ? DialogClose : DrawerClose;

  return <Component asChild={asChild}>{children}</Component>;
}
